import { useI18n } from "@/shared/i18n/LanguageContext";

export const MODEL_RUNTIME_ERROR_TITLE = "Model crashed";
export const MODEL_RUNTIME_ERROR_MESSAGE =
  "Model crashed. The repair team will handle it immediately.";

const MODEL_RUNTIME_ERROR_TOKENS = [
  "model worker",
  "worker_runtime_failures",
  "model_runtime_error",
  "model_subprocess_error",
  "model subprocess",
  "model task",
  "model choice unavailable",
];

type ErrorLike = {
  status?: unknown;
  code?: unknown;
  message?: unknown;
};

function readStatus(error: unknown): number | null {
  if (!error || typeof error !== "object" || !("status" in error)) {
    return null;
  }
  const status = (error as ErrorLike).status;
  return typeof status === "number" ? status : null;
}

function readErrorText(error: unknown): string {
  if (!error) {
    return "";
  }
  if (typeof error === "string") {
    return error;
  }
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === "object") {
    const errorLike = error as ErrorLike;
    return [errorLike.code, errorLike.message]
      .filter((value): value is string => typeof value === "string")
      .join(" ");
  }
  return "";
}

export function isModelRuntimeError(error: unknown): boolean {
  const status = readStatus(error);
  if (status !== null && status >= 500) {
    return true;
  }
  if (status === 503) {
    return true;
  }
  const text = readErrorText(error).toLowerCase();
  return MODEL_RUNTIME_ERROR_TOKENS.some((token) => text.includes(token));
}

export function resolveModelRuntimeErrorMessage(error: unknown, fallback: string): string {
  return isModelRuntimeError(error) ? MODEL_RUNTIME_ERROR_MESSAGE : fallback;
}

type ModelRuntimeErrorBlockProps = {
  compact?: boolean;
};

export function ModelRuntimeErrorBlock({ compact = false }: ModelRuntimeErrorBlockProps) {
  const { copy } = useI18n();
  return (
    <div className="model-runtime-error" data-compact={compact} role="status">
      <span className="model-runtime-error__mark" aria-hidden="true">
        !
      </span>
      <div>
        <strong>{copy.error.modelCrashedTitle}</strong>
        <p>{copy.error.modelCrashedMessage}</p>
      </div>
    </div>
  );
}
